import {
  type QueryDocumentSnapshot,
  type WriteBatch,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { actFromDoc, actToMap, newAct, type Act } from '@/models/act';
import { ttlTimestamp } from '@/lib/ttl';

// Firestore caps batches at 500 ops; 450 leaves headroom.
const BATCH_LIMIT = 450;

const actsCol = (uid: string) => collection(db, 'users', uid, 'acts');
const actDoc = (uid: string, id: string) => doc(db, 'users', uid, 'acts', id);

async function commitInChunks(
  docs: QueryDocumentSnapshot[],
  apply: (batch: WriteBatch, snap: QueryDocumentSnapshot) => void,
): Promise<void> {
  for (let i = 0; i < docs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const d of docs.slice(i, i + BATCH_LIMIT)) apply(batch, d);
    await batch.commit();
  }
}

export async function writeAct(uid: string, act: Act): Promise<void> {
  await setDoc(
    actDoc(uid, act.id),
    { ...actToMap(act), ttl: ttlTimestamp() },
    { merge: true },
  );
}

export async function readActsForPatient(args: {
  uid: string;
  patientId: string;
}): Promise<Act[]> {
  const q = query(
    actsCol(args.uid),
    where('patientId', '==', args.patientId),
    orderBy('billedAt', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => actFromDoc(d.id, d.data()));
}

export async function deleteAct(uid: string, actId: string): Promise<void> {
  await deleteDoc(actDoc(uid, actId));
}

export async function updateActFields(args: {
  uid: string;
  actId: string;
  fields: Partial<Act>;
}): Promise<void> {
  await updateDoc(actDoc(args.uid, args.actId), {
    ...args.fields,
    ttl: ttlTimestamp(),
  });
}

export async function finalizeActs(args: {
  uid: string;
  finalShiftId: string;
}): Promise<void> {
  const q = query(actsCol(args.uid), where('shiftId', '==', 'current_shift'));
  const snap = await getDocs(q);
  const ttl = ttlTimestamp();
  await commitInChunks(snap.docs, (batch, d) =>
    batch.update(d.ref, { shiftId: args.finalShiftId, ttl }),
  );
}

export async function deleteActsOfPatient(args: {
  uid: string;
  patientId: string;
}): Promise<void> {
  const q = query(actsCol(args.uid), where('patientId', '==', args.patientId));
  const snap = await getDocs(q);
  await commitInChunks(snap.docs, (batch, d) => batch.delete(d.ref));
}

export { newAct };
